import React from 'react';
import styled from '@emotion/native';
import { Linking, TouchableOpacity } from 'react-native';
import { BodyText, H2 } from '@/shared/components/StyledText';
import {
  darkGreen,
  green,
  welcomeKeepInTouchBackground,
  welcomeKeepInTouchText,
} from '@/shared/styles/colors';
import { IS_WEB } from '@/shared/constants/utility';
import { useWebHoverToggle } from '@/shared/hooks/useWebHoverToggle';
import { useWelcomeSectionLayout } from '@/shared/hooks/useWelcomeSectionLayout';
import KeepInTouchInstagramGrid from './KeepInTouchInstagramGrid';
import {
  FONT_STYLES,
  WELCOME_KEEP_IN_TOUCH_SECTION_PADDING_Y,
  WELCOME_SECTION_HORIZONTAL_BASE,
  WELCOME_SECTION_HORIZONTAL_RHYTHM_EXTRA,
  WELCOME_SECTION_MAX_WIDTH,
  WELCOME_SECTION_VERTICAL_BASE,
  WELCOME_SECTION_VERTICAL_RHYTHM_EXTRA,
} from '@/shared/styles/constants';

const INSTAGRAM_PROFILE_URL = 'https://www.instagram.com/emmestyling/';

interface SectionInnerProps {
  isWideGrid: boolean;
}

const MOBILE_TIGHT_SECTION_BOTTOM = 28;

const WelcomeKeepInTouchSection = () => {
  const { isWide: isWideGrid } = useWelcomeSectionLayout();
  const { isHovered: isSubtitleHovered, webHoverProps: subtitleWebHoverProps } =
    useWebHoverToggle();

  return (
    <Section isCompactLayout={!isWideGrid}>
      <SectionInner isWideGrid={isWideGrid}>
        <SectionTitle>keep in touch</SectionTitle>
        <SubtitleLink
          onPress={() => Linking.openURL(INSTAGRAM_PROFILE_URL)}
          accessibilityRole="link"
          accessibilityLabel="Follow Emme Styling on Instagram @emmestyling"
          {...subtitleWebHoverProps}
        >
          <SubtitleText
            style={
              IS_WEB && isSubtitleHovered ? { color: darkGreen } : undefined
            }
          >
            Follow us @emmestyling
          </SubtitleText>
        </SubtitleLink>
        <KeepInTouchInstagramGrid />
      </SectionInner>
    </Section>
  );
};

interface SectionProps {
  isCompactLayout: boolean;
}

const Section = styled.View<SectionProps>(({ isCompactLayout }) => ({
  width: '100%',
  backgroundColor: welcomeKeepInTouchBackground,
  paddingTop: isCompactLayout
    ? WELCOME_SECTION_VERTICAL_BASE + WELCOME_SECTION_VERTICAL_RHYTHM_EXTRA
    : WELCOME_KEEP_IN_TOUCH_SECTION_PADDING_Y,
  paddingBottom: isCompactLayout
    ? MOBILE_TIGHT_SECTION_BOTTOM
    : WELCOME_KEEP_IN_TOUCH_SECTION_PADDING_Y,
}));

const SectionInner = styled.View<SectionInnerProps>(({ isWideGrid }) => ({
  width: '100%',
  maxWidth: isWideGrid ? WELCOME_SECTION_MAX_WIDTH : undefined,
  alignSelf: 'center',
  paddingHorizontal: isWideGrid
    ? WELCOME_SECTION_HORIZONTAL_BASE
    : WELCOME_SECTION_HORIZONTAL_RHYTHM_EXTRA,
}));

const SectionTitle = styled(H2)({
  color: welcomeKeepInTouchText,
  textAlign: 'center',
  textTransform: 'uppercase',
  marginBottom: 12,
  letterSpacing: 1,
  fontSize: 30,
  lineHeight: 36,
});

const SubtitleText = styled(BodyText)({
  ...FONT_STYLES.welcomeHelveticaNeue,
  color: green,
  textAlign: 'center',
  marginBottom: 0,
  fontSize: 16,
  lineHeight: 22,
  letterSpacing: 0.9,
});

const SubtitleLink = styled(TouchableOpacity)({
  alignSelf: 'center',
  marginBottom: 32,
});

export default WelcomeKeepInTouchSection;
